import { screenshotProducts } from './systemsCatalog'
import { enrichProduct, sanitizeSpecGroups } from './productDetailEnrichment'
import { enrichFeatureSection } from './categoryFeatureEnrichment'

const img = (file) => `/images/brochure/${file}`
const pdfPage = (n) => `/images/pdf-pages/gaoren-${String(n).padStart(2, '0')}.png`

function buildProducts(items, categoryConfig) {
  const categoryStub = {
    id: categoryConfig.id,
    label: categoryConfig.label,
    parentLabel: categoryConfig.parentLabel,
    heroDesc: categoryConfig.heroDesc,
    featureBullets: categoryConfig.featureBullets,
    specType: categoryConfig.specType,
    detailMode: categoryConfig.detailMode,
  }
  return items.map((item, index) =>
    enrichProduct(categoryStub, {
      tag: '产品',
      slug: item.slug || String(index),
      ...item,
    })
  )
}

function buildFeatureSections(config) {
  if (config.featureSections?.length) {
    return config.featureSections
  }

  const bullets = config.featureBullets || []
  const isEquipment =
    config.specType === 'education' && config.detailMode === 'equipment'

  const defaultFeatures = [
    { icon: 'controller', label: '核心能力' },
    { icon: 'software', label: '功能范围' },
    { icon: 'nav', label: '应用场景' },
  ]

  const equipmentFeatures =
    bullets.length === 3
      ? [
          { icon: 'controller', label: '技术参数' },
          { icon: 'software', label: '实验项目' },
          { icon: 'nav', label: '适用场景' },
        ]
      : [
          { icon: 'controller', label: '技术参数' },
          { icon: 'software', label: '结构配置' },
          { icon: 'nav', label: '安全与适用' },
        ]

  const baseFeatures = config.features || (isEquipment ? equipmentFeatures : defaultFeatures)

  if (bullets.length === baseFeatures.length) {
    return baseFeatures.map((feat, i) =>
      enrichFeatureSection(
        { ...feat, bullets: [bullets[i]] },
        bullets[i],
        config,
        i
      )
    )
  }

  if (isEquipment && bullets.length === 4) {
    const grouped = [
      { ...baseFeatures[0], bullets: bullets.slice(0, 2) },
      { ...baseFeatures[1], bullets: [bullets[2]] },
      { ...baseFeatures[2], bullets: [bullets[3]] },
    ]
    return grouped.map((section, i) =>
      enrichFeatureSection(section, section.bullets[0], config, i)
    )
  }

  if (bullets.length > baseFeatures.length) {
    const chunkSize = Math.ceil(bullets.length / baseFeatures.length)
    return baseFeatures.map((feat, i) => {
      const chunk = bullets.slice(i * chunkSize, (i + 1) * chunkSize)
      return enrichFeatureSection({ ...feat, bullets: chunk }, chunk[0], config, i)
    })
  }

  return baseFeatures.map((feat, i) =>
    enrichFeatureSection(
      { ...feat, bullets: i === 0 ? bullets : [] },
      bullets[i] || bullets[0] || '',
      config,
      i
    )
  )
}

function createCategory(config) {
  const features = buildFeatureSections(config)
  return {
    featureTabs: ['全部'],
    activeTab: '全部',
    detailMode: config.detailMode || 'service',
    loadLabel: config.loadLabel,
    modelLabel: config.modelLabel,
    ...config,
    features,
    products: buildProducts(config.products, config),
  }
}

export const productSidebar = [
  {
    id: 'system',
    label: '系统类',
    children: [
      { id: 'software', label: '工业软件系统定制' },
      { id: 'energy-system', label: '电能风能与新能源系统' },
      { id: 'iiot', label: '工业互联网平台' },
    ],
  },
  {
    id: 'education',
    label: '教育类',
    children: [
      { id: 'smart-line', label: '智能智造产线应用综合实训平台' },
      { id: 'estun-trainer', label: '工业机器人技能实训装置（埃斯顿）' },
      { id: 'auto-line-trainer', label: '智能制造自动化生产线综合实训系统' },
      { id: 'robot-ops', label: '工业机器人系统运维实训平台' },
      { id: 'sensor-box', label: '典型传感器技术实验箱' },
      { id: 'sensor-lab', label: '工业传感器综合实验装置' },
      { id: 'electrician', label: '高级电工实训考核装置' },
      { id: 'electric-electronic', label: '电工电子技术综合实验装置' },
      { id: 'network-platform', label: '生产系统网络与通讯运维实践平台' },
      { id: 'plc-trainer', label: '可编程控制器实训装置' },
      { id: 'mechanical-assembly', label: '机械装调技能实训考核装置' },
      { id: 'welding-station', label: '焊接机器人工作站' },
      { id: 'digital-resource', label: '数字资源定制开发' },
      { id: 'digital-textbook', label: '数字教材定制开发出版' },
      { id: 'teaching-library', label: '专业教学资源库开发' },
      { id: 'competition-support', label: '技能大赛技术支持与专家指导' },
      { id: 'research-cooperation', label: '高校及职业院校科研技术合作' },
    ],
  },
  {
    id: 'robotics',
    label: '机器人类',
    children: [
      { id: 'humanoid', label: '人型机器人' },
      { id: 'quadruped', label: '运维机器狗' },
      { id: 'ai-custom', label: '人工智能技术定制开发' },
      { id: 'drone', label: '无人机产品定制开发' },
    ],
  },
  {
    id: 'agv-group',
    label: 'AGV类',
    children: [
      { id: 'agv', label: 'AGV产品定制开发' },
      { id: 'mobile-platform', label: '移动机器人产品定制开发' },
    ],
  },
  {
    id: 'automation',
    label: '非标自动化类',
    children: [
      { id: 'automation-line', label: '自动化产线非标定制' },
      { id: 'original-products', label: '原创工业级产品' },
      { id: 'case-showcase', label: '案例效果图' },
      { id: 'service-clients', label: '主要服务客户' },
    ],
  },
]

export const productCategories = {
  software: createCategory({
    id: 'software',
    specType: 'system',
    featureTabs: ['全部', '智慧用电云平台'],
    label: '工业软件系统定制',
    parentLabel: '系统类',
    heroTitle: '工业软件系统与数字化平台',
    heroDesc:
      '结合宣传册公司简介与工业软件页内容，集中展示 OA、MES、ERP、WMS、数字孪生、点检巡检和节能管理等系统类能力。',
    heroImage: pdfPage(9),
    featureBullets: [
      '支持 MES、ERP、WMS、OA 等企业管理系统定制开发',
      '支持数字孪生、产线仿真、数据可视化和看板系统建设',
      '支持预见性故障监测、点检系统、节能管理与本地化部署',
    ],
    products: [
      ...screenshotProducts('smart-power-cloud'),
      {
        name: 'MES 生产制造执行系统',
        desc: '围绕生产计划、工艺执行、质量追溯和现场数据采集构建制造执行平台。',
        image: pdfPage(9),
        imageFallback: img('img-p9-5.jpeg'),
        load: '模块化部署',
        model: '制造执行平台',
        tagline: '面向制造企业的生产执行、追溯与数据联动平台。',
        specGroups: [
          {
            name: '系统能力',
            rows: [
              ['核心模块', '工单管理、工艺执行、质量追溯、数据采集'],
              ['部署方式', '本地部署 / 私有化部署 / 混合部署'],
              ['适用场景', '离散制造、装配产线、教学示范工厂'],
            ],
          },
          {
            name: '扩展参数',
            rows: [
              ['接口对接', 'PLC / 机器人 / WMS / ERP / 条码追溯'],
              ['用户规模', '待补充'],
              ['数据量级', '待补充'],
            ],
          },
        ],
      },
      {
        name: 'ERP 企业经营管理系统',
        desc: '围绕采购、库存、财务、订单和经营数据协同构建企业管理中台。',
        image: img('img-p9-3.jpeg'),
        imageFallback: pdfPage(9),
        load: '业务系统定制',
        model: '经营管理平台',
        specGroups: [
          {
            name: '系统能力',
            rows: [
              ['核心模块', '采购管理、库存管理、订单管理、财务协同'],
              ['适用组织', '制造企业、设备公司、项目型组织'],
              ['部署方式', '本地 / 云端 / 混合模式'],
            ],
          },
          {
            name: '扩展参数',
            rows: [
              ['并发用户', '待补充'],
              ['审批流引擎', '待补充'],
              ['数据报表', '待补充'],
            ],
          },
        ],
      },
      {
        name: 'WMS 仓储物流管理系统',
        desc: '面向仓储物流与 AGV 调度联动的仓储管理系统，可承接入库、出库、库位与任务协同。',
        image: img('img-p10-5.jpeg'),
        imageFallback: pdfPage(9),
        load: '仓储系统定制',
        model: '物流协同平台',
        specGroups: [
          {
            name: '系统能力',
            rows: [
              ['核心模块', '入库管理、出库管理、库位管理、任务调度'],
              ['联动对象', 'AGV / AMR / 条码系统 / 上位业务系统'],
              ['适用场景', '仓内转运、校企实训仓储、物料配送'],
            ],
          },
          {
            name: '扩展参数',
            rows: [
              ['接口协议', '待补充'],
              ['库位规模', '待补充'],
              ['调度策略', '待补充'],
            ],
          },
        ],
      },
      {
        name: 'OA 协同办公系统',
        desc: '围绕组织审批、流程流转、文档与协同办公搭建企业数字办公平台。',
        image: img('img-p9-5.jpeg'),
        imageFallback: pdfPage(9),
        load: '协同系统定制',
        model: '办公协同平台',
        specGroups: [
          {
            name: '系统能力',
            rows: [
              ['核心模块', '审批流、公告、文档、资产、流程中心'],
              ['适用对象', '企业管理、项目审批、跨部门协同'],
              ['部署方式', '本地部署 / 私有云'],
            ],
          },
          {
            name: '扩展参数',
            rows: [
              ['移动端支持', '待补充'],
              ['单点登录', '待补充'],
              ['权限体系', '待补充'],
            ],
          },
        ],
      },
      {
        name: '数字孪生与可视化平台',
        desc: '将生产线、设备和运行数据映射为可视化数字模型，服务于展示、监控与教学演示。',
        image: pdfPage(10),
        imageFallback: img('img-p10-3.jpeg'),
        load: '本地 / 云端',
        model: '数字孪生平台',
        specGroups: [
          {
            name: '系统能力',
            rows: [
              ['核心模块', '三维可视化、运行监控、告警看板、数据联动'],
              ['适用场景', '数字化车间、实训室、示范项目展示'],
              ['数据来源', 'PLC、传感器、机器人、工业网络'],
            ],
          },
          {
            name: '扩展参数',
            rows: [
              ['刷新频率', '待补充'],
              ['建模范围', '待补充'],
              ['渲染终端', '待补充'],
            ],
          },
        ],
      },
      {
        name: '预见性故障监测与点检系统',
        desc: '支持设备点检、状态监测、历史趋势与故障预警，适合运维和教学场景。',
        image: img('img-p10-3.jpeg'),
        imageFallback: pdfPage(10),
        load: '运维系统定制',
        model: '监测诊断平台',
        specGroups: [
          {
            name: '系统能力',
            rows: [
              ['功能范围', '点检任务、状态监测、趋势分析、预警提醒'],
              ['适用对象', '设备运维、产线巡检、教学示范'],
              ['联动数据', '振动、温度、电流、电压、环境变量'],
            ],
          },
          {
            name: '扩展参数',
            rows: [
              ['采样周期', '待补充'],
              ['告警规则', '待补充'],
              ['历史存储时长', '待补充'],
            ],
          },
        ],
      },
      {
        name: '节能管理系统',
        desc: '围绕能耗采集、能源看板、能效分析与节能改造建议提供数字化管理能力。',
        image: pdfPage(10),
        imageFallback: img('img-p10-5.jpeg'),
        load: '能源管理定制',
        model: '能效分析平台',
        specGroups: [
          {
            name: '系统能力',
            rows: [
              ['功能范围', '能耗采集、分项统计、趋势分析、告警报表'],
              ['适用场景', '工厂、园区、实训基地、公共建筑'],
              ['联动对象', '电表、水表、气表、能源监控设备'],
            ],
          },
          {
            name: '扩展参数',
            rows: [
              ['采集点位', '待补充'],
              ['报表周期', '待补充'],
              ['节能策略', '待补充'],
            ],
          },
        ],
      },
    ],
  }),

  'energy-system': createCategory({
    id: 'energy-system',
    specType: 'system',
    featureTabs: ['全部', '智慧光伏发电云平台'],
    label: '电能风能与新能源系统',
    parentLabel: '系统类',
    heroTitle: '电能、风能与新能源应用系统',
    heroDesc:
      '结合宣传册中的新能源类内容与公司产品线说明，覆盖太阳能实验系统、能源监控、风能教学与新能源管理平台。',
    heroImage: pdfPage(35),
    featureBullets: [
      '支持太阳能光伏、风能教学与能源监控场景建设',
      '支持模拟光源跟踪、双轴控制、能效采集与数据可视化',
      '缺失参数统一预留占位，便于后续补充真实规格',
    ],
    products: [
      ...screenshotProducts('smart-pv-cloud'),
      {
        name: '太阳能光伏发电实验装置',
        desc: '由模拟光源跟踪装置和模拟能源控制系统组成，可用于演示、设计、安装和调试。',
        image: img('img-p15-4.jpeg'),
        load: '＜3kVA',
        model: '双轴跟踪系统',
        specGroups: [
          {
            name: '技术指标',
            rows: [
              ['输入电压', '三相四线～380V±10% 50Hz'],
              ['装置容量', '＜3kVA'],
              ['太阳能电池板', '20W×4，调节高度300mm'],
              ['模拟光源功率', '1000W'],
              ['跟踪方式', '双轴，俯仰180度，旋转360度，跟踪精度＜±1.5度'],
            ],
          },
        ],
      },
      {
        name: '风能发电教学与监控系统',
        desc: '面向新能源教学与实验室建设的风能发电教学系统，可与能源管理平台联动。',
        image: pdfPage(35),
        load: '待补充',
        model: '风能教学系统',
        specGroups: [
          {
            name: '占位参数',
            rows: [
              ['额定功率', '待补充'],
              ['输入电源', '待补充'],
              ['装置尺寸', '待补充'],
              ['监控方式', '待补充'],
            ],
          },
        ],
      },
      {
        name: '新能源实验室综合管理平台',
        desc: '用于新能源实训室的设备联动、数据看板和教学演示，适合实训基地建设。',
        image: pdfPage(35),
        load: '平台化部署',
        model: '实验室管理系统',
        specGroups: [
          {
            name: '系统能力',
            rows: [
              ['功能范围', '设备接入、数据采集、能效分析、实验看板'],
              ['适用对象', '新能源实训室、职业院校、示范基地'],
              ['参数状态', '详细规格待补充'],
            ],
          },
        ],
      },
    ],
  }),

  iiot: createCategory({
    id: 'iiot',
    specType: 'system',
    label: '工业互联网平台',
    parentLabel: '系统类',
    heroTitle: '工业互联网与数据采集平台',
    heroDesc:
      '宣传册中明确提到环境监测、能源监控、数据追溯、上云与工业 APP 能力，这部分统一放在工业互联网平台类目中。',
    heroImage: img('img-p17-2.jpeg'),
    featureBullets: [
      '支持设备接入、数据采集、上云、可视化与数字孪生',
      '适合环境监测、能源监控、生产过程监控和数据追溯',
      '可训练工业 APP 开发、私有化部署和网关联动能力',
    ],
    products: [
      {
        name: '工业互联网综合实训平台',
        desc: '以多种传感器、采集模块、网关与计算机为基础搭建工业互联网教学与展示平台。',
        image: img('img-p17-2.jpeg'),
        load: '＜2.0kVA',
        model: '1880×880×1950mm',
        specGroups: [
          {
            name: '技术指标',
            rows: [
              ['交流电源', '单相～220V±10% 50Hz'],
              ['装置容量', '＜2.0kVA'],
              ['工作环境', '温度-10℃～+40℃，相对湿度≤90%'],
              ['外形尺寸', '约1880mm×880mm×1950mm'],
              ['安全保护', '漏电保护、智能重合闸电源保护、接地保护'],
            ],
          },
        ],
      },
      {
        name: '工业 APP 与数据追溯平台',
        desc: '面向生产过程监控、环境监测和能源管理的应用层平台，可与前端采集设备联动。',
        image: pdfPage(17),
        load: '平台定制',
        model: '工业 APP 平台',
        specGroups: [
          {
            name: '系统能力',
            rows: [
              ['功能范围', '环境监测、能源监控、数据追溯、可视化看板'],
              ['部署方式', '私有化本地部署'],
              ['接口能力', '485采集模块、PLC、网关、无线路由、计算机'],
            ],
          },
          {
            name: '扩展参数',
            rows: [
              ['APP 数量', '待补充'],
              ['终端类型', '待补充'],
              ['数据存储规模', '待补充'],
            ],
          },
        ],
      },
    ],
  }),

  humanoid: createCategory({
    id: 'humanoid',
    specType: 'robotics',
    label: '人型机器人',
    parentLabel: '机器人类',
    heroTitle: '人型机器人',
    heroDesc: '面向服务、展示、科研与教学等场景的人型机器人方案，覆盖双足与移动人型两类方向。',
    heroImage: img('img-p4-18.jpeg'),
    featureBullets: [
      '支持双足与移动人型整机定制开发',
      '可扩展视觉交互、语音交互与二次开发接口',
      '适用于展厅、园区、实验室和教学场景',
    ],
    products: [
      {
        name: '移动人型机器人（PL-UNIVERSE）',
        desc: '双臂移动人型机器人整机方案。',
        image: img('img-p4-18.jpeg'),
        load: '定制开发',
        model: '移动人型方案',
      },
      {
        name: '双足人型机器人（UNITREE）',
        desc: '双足人型机器人整机方案。',
        image: img('img-p4-19.jpeg'),
        load: '定制开发',
        model: '双足人型方案',
      },
    ],
  }),

  quadruped: createCategory({
    id: 'quadruped',
    specType: 'robotics',
    label: '运维机器狗',
    parentLabel: '机器人类',
    heroTitle: '运维机器狗',
    heroDesc: '面向电力、石化、园区巡检和复杂地形运维任务的四足机器人方案。',
    heroImage: img('embed-p4-15.jpeg'),
    featureBullets: [
      '可适应楼梯、坡道等复杂地形',
      '支持可见光、红外、气体检测等多种载荷',
      '支持远程操控、任务规划与巡检数据回传',
    ],
    products: [
      {
        name: '四足巡检机器狗（浅灰款）',
        desc: '工业级四足机器人巡检方案。',
        image: img('embed-p4-15.jpeg'),
        load: '定制开发',
        model: '四足巡检型',
      },
      {
        name: '四足巡检机器狗（轮足款）',
        desc: '轮足复合四足机器人方案。',
        image: img('embed-p4-17.jpeg'),
        load: '定制开发',
        model: '轮足巡检型',
      },
    ],
  }),

  'ai-custom': createCategory({
    id: 'ai-custom',
    specType: 'robotics',
    label: '人工智能技术定制开发',
    parentLabel: '机器人类',
    heroTitle: '人工智能技术定制开发',
    heroDesc: '围绕 AI 实训箱、控制器、视觉与嵌入式平台，支持算法验证、机器人应用集成和教学研发。',
    heroImage: img('embed-p4-13.jpeg'),
    featureBullets: [
      '支持机器视觉、深度学习、嵌入式 AI 多方向定制',
      '适配便携式、桌面式、立式平台等多种产品形态',
      '可与机器人、产线设备和教学平台深度集成',
    ],
    products: [
      {
        name: '便携式机器人实训箱',
        desc: '集成机械臂、视觉与示教屏幕的 AI 实训方案。',
        image: img('embed-p4-3.jpeg'),
        load: '定制开发',
        model: '便携实训箱',
      },
      {
        name: '便携式 AI 开发箱（蓝面板）',
        desc: '集成机械臂和 AI 开发界面的便携式平台。',
        image: img('embed-p4-5.jpeg'),
        load: '定制开发',
        model: 'AI 开发箱 A',
      },
      {
        name: '嵌入式 AI 开发箱',
        desc: '适合嵌入式算法和控制系统联调。',
        image: img('embed-p4-7.jpeg'),
        load: '定制开发',
        model: 'AI 开发箱 B',
      },
      {
        name: 'Delta 机器人实训工作站',
        desc: '并联机器人实训与开发平台。',
        image: img('embed-p4-11.jpeg'),
        load: '定制开发',
        model: 'Delta 工作站',
      },
      {
        name: 'AI 实训平台',
        desc: '竖式 AI 实训与演示平台。',
        image: img('embed-p4-13.jpeg'),
        load: '定制开发',
        model: 'AI 实训平台',
      },
      {
        name: '移动 AI 开发平台',
        desc: '小型移动 AI 开发底盘，适合算法和机器人联调。',
        image: '/images/pathway/mobile-platform.png',
        imageFallback: '/images/amr-chassis.png',
        load: '定制开发',
        model: '移动开发平台',
      },
    ],
  }),

  drone: createCategory({
    id: 'drone',
    specType: 'robotics',
    label: '无人机产品定制开发',
    parentLabel: '机器人类',
    heroTitle: '无人机产品定制开发',
    heroDesc: '围绕工业巡检、物流运输和多载荷任务展开的无人机定制开发服务。',
    heroImage: img('img-p6-4.png'),
    featureBullets: [
      '适用于电力、石化、园区、物流等应用方向',
      '可定制相机、激光雷达、气体检测等任务载荷',
      '提供地面站软件、任务规划与数据分析服务',
    ],
    products: [
      {
        name: '重载物流无人机',
        desc: '大载荷工业级多旋翼无人机。',
        image: img('img-p6-4.png'),
        load: '定制载荷',
        model: '重载物流型',
      },
      {
        name: '工业巡检无人机',
        desc: '多镜头工业巡检无人机。',
        image: img('img-p6-5.png'),
        load: '定制载荷',
        model: '工业巡检型',
      },
    ],
  }),

  agv: createCategory({
    id: 'agv',
    specType: 'agv',
    label: 'AGV产品定制开发',
    parentLabel: 'AGV类',
    heroTitle: 'AGV 产品定制开发',
    heroDesc: '包含潜伏顶升底盘、复合移动机器人、智能叉车和辊筒搬运等多种 AGV 形态。',
    // 宣传册 PDF 第 5 页「AGV产品定制开发」内嵌图（按文件体积筛掉 <1KB 空白占位）
    heroImage: img('img-p5-9.png'),
    featureBullets: [
      '支持 SLAM 导航、多机调度与 WMS / MES 对接',
      '潜伏顶升、智能叉车、复合机器人等多种形态可选',
      '提供从方案设计到交付运维的全流程服务',
    ],
    products: [
      {
        name: '潜伏顶升 AGV 底盘',
        desc: '低轮廓 AGV 移动底盘。',
        image: img('embed-p5-1.jpeg'),
        imageFallback: img('img-p5-1.jpeg'),
        load: '定制负载',
        model: '顶升底盘',
      },
      {
        name: '复合移动机器人',
        desc: 'AGV 底盘与协作机械臂一体化方案。',
        image: img('embed-p5-9.png'),
        imageFallback: img('img-p5-9.png'),
        load: '定制负载',
        model: '复合机器人',
      },
      {
        name: '智能叉车 AGV',
        desc: '自动叉取与堆垛作业方案。',
        image: img('embed-p5-4.jpeg'),
        imageFallback: img('img-p5-4.jpeg'),
        load: '定制负载',
        model: '智能叉车',
      },
      {
        name: '辊筒搬运 AGV',
        desc: '辊筒顶升物料搬运平台。',
        image: img('embed-p5-6.jpeg'),
        imageFallback: img('img-p5-6.jpeg'),
        load: '定制负载',
        model: '辊筒搬运',
      },
    ],
  }),

  'mobile-platform': createCategory({
    id: 'mobile-platform',
    specType: 'agv',
    label: '移动机器人产品定制开发',
    parentLabel: 'AGV类',
    heroTitle: '移动机器人产品定制开发',
    heroDesc: '提供 AMR 底盘、移动平台、复合机器人和移动开发平台等开放式底盘方案。',
    // 宣传册 PDF 第 6 页「移动机器人产品定制开发」内嵌图
    heroImage: img('embed-p6-5.png'),
    featureBullets: [
      '模块化底盘设计，支持多种驱动形式',
      '开放 SDK 接口，便于二次开发集成',
      '可与机械臂、视觉、语音等上装设备结合',
    ],
    products: [
      {
        name: '标准 AMR 移动底盘',
        desc: '通用移动机器人底盘平台。',
        image: img('embed-p6-6.png'),
        imageFallback: img('img-p6-6.png'),
        load: '定制负载',
        model: 'AMR 底盘',
      },
      {
        name: '全向移动平台',
        desc: '适合室内灵活部署的移动平台。',
        image: img('embed-p6-5.png'),
        imageFallback: img('img-p6-5.png'),
        load: '定制负载',
        model: '移动平台',
      },
      {
        name: '移动复合机器人',
        desc: '移动底盘与协作臂一体方案。',
        image: img('embed-p6-4.png'),
        imageFallback: img('img-p6-4.png'),
        load: '定制负载',
        model: '复合机器人',
      },
    ],
  }),

  'automation-line': createCategory({
    id: 'automation-line',
    specType: 'automation',
    label: '自动化产线非标定制',
    parentLabel: '非标自动化类',
    heroTitle: '自动化产线非标定制',
    heroDesc: '宣传册展示了装配、检测、包装等非标产线与自动化工位方案，适合整体线体与单元定制。',
    heroImage: pdfPage(7),
    featureBullets: [
      '从工艺分析、方案设计到设备制造与调试交付',
      '集成机器人、视觉、传感器、输送线和工业软件系统',
      '支持柔性生产、信息化联动和工厂升级改造',
    ],
    products: [
      {
        name: '自动化产线非标定制（1）',
        desc: '模块化自动装配产线方案。',
        image: img('img-p7-5.jpeg'),
        load: '非标定制',
        model: '装配产线',
      },
      {
        name: '自动化产线非标定制（2）',
        desc: '包装检测一体化产线方案。',
        image: img('img-p8-5.jpeg'),
        load: '非标定制',
        model: '包装产线',
      },
      {
        name: '自动化专机与工位集成',
        desc: '围绕装配、检测、上料、包装等工序进行单元化工位开发。',
        image: pdfPage(8),
        load: '定制开发',
        model: '自动化专机',
        specGroups: [
          {
            name: '交付内容',
            rows: [
              ['服务范围', '工艺分析、机械设计、电控设计、集成调试、现场交付'],
              ['对接系统', 'MES / WMS / 机器人 / 视觉 / 传感器'],
              ['详细尺寸', '待补充'],
            ],
          },
        ],
      },
    ],
  }),

  'original-products': createCategory({
    id: 'original-products',
    specType: 'automation',
    label: '原创工业级产品',
    parentLabel: '非标自动化类',
    heroTitle: '原创工业级产品',
    heroDesc: '宣传册第三部分展示了当前原创工业级产品，可作为独立模块或与产线系统组合交付。',
    heroImage: pdfPage(29),
    featureBullets: [
      '来源于公司原创技术研发与项目积累',
      '可作为工业场景独立交付或组合到产线项目中',
      '详细技术参数可在后续商务沟通中进一步补齐',
    ],
    products: [
      {
        name: '原创工业级产品（1）',
        desc: '宣传册第 26 页展示的原创工业级产品组合。',
        image: pdfPage(29),
        load: '原创研发',
        model: '工业级产品集',
        specGroups: [
          {
            name: '说明',
            rows: [
              ['内容来源', '宣传册第 26 页整页展示'],
              ['参数状态', '详细型号与规格待补充'],
              ['适用方向', '自动化、机器人、教学示范项目'],
            ],
          },
        ],
      },
      {
        name: '原创工业级产品（2）',
        desc: '宣传册第 27 页展示的原创工业级产品组合。',
        image: pdfPage(30),
        load: '原创研发',
        model: '工业级产品集',
        specGroups: [
          {
            name: '说明',
            rows: [
              ['内容来源', '宣传册第 27 页整页展示'],
              ['参数状态', '详细型号与规格待补充'],
              ['适用方向', '工业设备、教学装备、定制集成'],
            ],
          },
        ],
      },
    ],
  }),

  'case-showcase': createCategory({
    id: 'case-showcase',
    specType: 'automation',
    label: '案例效果图',
    parentLabel: '非标自动化类',
    heroTitle: '案例效果图与实训室建设案例',
    heroDesc: '宣传册第四部分包含机电一体化、数字化产线、工业机器人、电工电子、新能源与数字孪生实训室效果图。',
    heroImage: pdfPage(31),
    featureBullets: [
      '覆盖机电一体化、机器人、传感器、电工电子、新能源、数字孪生等场景',
      '可用于实训基地、示范教室和校企合作空间规划',
      '支持效果图深化、施工落地与成套设备配套',
    ],
    products: [
      {
        name: '机电一体化实训室效果图',
        desc: '宣传册案例效果图第 1 页展示的空间方案。',
        image: pdfPage(31),
        load: '空间规划',
        model: '实训室效果图',
      },
      {
        name: '数字化产线管理与运维实训基地效果图',
        desc: '数字化产线与运维基地的整体展示方案。',
        image: pdfPage(32),
        load: '空间规划',
        model: '基地效果图',
      },
      {
        name: '工业机器人与传感器实训室效果图',
        desc: '工业机器人实训室与传感器实训室组合案例。',
        image: pdfPage(33),
        load: '空间规划',
        model: '复合实训室',
      },
      {
        name: '电工电子与维修电工实训室效果图',
        desc: '电工电子与维修电工实训空间案例。',
        image: pdfPage(34),
        load: '空间规划',
        model: '电工实训室',
      },
      {
        name: '新能源与数字孪生仿真实训室效果图',
        desc: '新能源实训室与数字孪生仿真实训室案例。',
        image: pdfPage(35),
        load: '空间规划',
        model: '新能源 / 数字孪生',
      },
    ],
  }),

  'service-clients': createCategory({
    id: 'service-clients',
    specType: 'automation',
    label: '主要服务客户',
    parentLabel: '非标自动化类',
    heroTitle: '主要服务客户与合作对象',
    heroDesc: '宣传册第五部分给出了企业及科协、街道社区、职业院校和本科院校等主要服务客户范围。',
    heroImage: pdfPage(36),
    featureBullets: [
      '服务对象覆盖企业、院校、科协、社区与公共服务机构',
      '体现高仁科技在职业教育、系统开发和项目交付方面的服务广度',
      '页面保留代表性客户示例，完整名单可继续补充',
    ],
    products: [
      {
        name: '企业及科协服务客户',
        desc: '包含杭州蔚来科教、江苏华明、天津钢管公司、天津市科技馆等代表客户。',
        image: img('img-p36-2.jpeg'),
        imageFallback: pdfPage(36),
        listImageFocus: 'top',
        load: '客户案例',
        model: '企业与科协',
        specGroups: [
          {
            name: '代表客户',
            rows: [
              ['企业客户', '杭州蔚来科教、江苏华明、天津钢管公司、天津方舟科技等'],
              ['科协客户', '天津市东丽区科协、天津市南开区科协、天津市北辰区科协等'],
              ['完整名单', '可根据后续资料继续扩充'],
            ],
          },
        ],
      },
      {
        name: '职业院校与本科院校服务客户',
        desc: '覆盖天津市职业大学、天津机电职业技术学院、天津大学、南开大学等院校。',
        image: pdfPage(36),
        imageFallback: img('img-p36-2.jpeg'),
        listImageFocus: 'center 72%',
        load: '客户案例',
        model: '院校客户',
        specGroups: [
          {
            name: '代表院校',
            rows: [
              ['职业院校', '天津市职业大学、天津轻工职业技术学院、天津机电职业技术学院等'],
              ['本科院校', '天津大学、南开大学、天津工业大学、河北工业大学等'],
              ['服务方向', '实训建设、系统开发、竞赛支持、科研合作'],
            ],
          },
        ],
      },
    ],
  }),

  'smart-line': createCategory({
    id: 'smart-line',
    specType: 'education',
    detailMode: 'equipment',
    label: '智能智造产线应用综合实训平台',
    parentLabel: '教育类',
    heroTitle: '智能智造产线应用综合实训平台',
    heroDesc:
      '设备综合工业机器人应用技术、数字孪生技术、PLC 控制技术、机器视觉技术、RFID 技术、组态技术等相关技术。',
    heroImage: img('img-p11-3.jpeg'),
    featureBullets: [
      '交流电源：单相三线～220V±10% 50Hz',
      '装置容量：≤2.2kVA',
      '设备尺寸：3300mm×1000mm×1700mm（L×W×H）',
      '安全保护功能：急停按钮，漏电保护，过流保护',
    ],
    products: [
      {
        name: '智能智造产线应用综合实训平台',
        desc: '六轴机器人核心智能制造产线，适合教学与竞赛平台。',
        image: img('img-p11-3.jpeg'),
        load: '≤2.2kVA',
        model: '3300×1000×1700mm',
      },
    ],
  }),

  'estun-trainer': createCategory({
    id: 'estun-trainer',
    specType: 'education',
    detailMode: 'equipment',
    label: '工业机器人技能实训装置（埃斯顿）',
    parentLabel: '教育类',
    heroTitle: '工业机器人技能实训装置（埃斯顿）',
    heroDesc:
      '以六轴工业机器人为核心，包含搬运码垛、分拣、抛光打磨、装配和焊接等典型工业机器人应用工艺。',
    heroImage: img('img-p11-5.jpeg'),
    featureBullets: [
      '输入电源：AC220V±10％ 50Hz',
      '装置容量：＜3.5kVA',
      '平台尺寸：1760mm×960mm×1880mm',
      '安全保护：具有漏电保护，安全符合国家标准',
    ],
    products: [
      {
        name: '工业机器人技能实训装置（埃斯顿）',
        desc: '面向工业机器人操作与编程教学的实训装置。',
        image: img('img-p11-5.jpeg'),
        load: '＜3.5kVA',
        model: '1760×960×1880mm',
      },
    ],
  }),

  'auto-line-trainer': createCategory({
    id: 'auto-line-trainer',
    specType: 'education',
    detailMode: 'equipment',
    label: '智能制造自动化生产线综合实训系统',
    parentLabel: '教育类',
    heroTitle: '智能制造自动化生产线综合实训系统',
    heroDesc:
      '以传感器生产线为载体，由推板上料、振动上料、翻转机械手、协作机器人、立体仓库等组成。',
    heroImage: img('img-p12-3.jpeg'),
    featureBullets: [
      '输入电源：AC220V±10％ 50Hz',
      '装置容量：＜2kVA',
      '平台尺寸：2400mm×960mm×1780mm',
      '安全保护：具有漏电保护，安全符合国家标准',
    ],
    products: [
      {
        name: '智能制造自动化生产线综合实训系统',
        desc: '实现装配、翻转、搬运、传输、检测、包装与入库等全过程实训。',
        image: img('img-p12-3.jpeg'),
        load: '＜2kVA',
        model: '2400×960×1780mm',
      },
    ],
  }),

  'robot-ops': createCategory({
    id: 'robot-ops',
    specType: 'education',
    detailMode: 'equipment',
    label: '工业机器人系统运维实训平台',
    parentLabel: '教育类',
    heroTitle: '工业机器人系统运维实训平台',
    heroDesc: '由工业机器人本体、机器人底座、控制柜和示教盒组成，面向工业机器人运维教学。',
    heroImage: img('img-p12-5.jpeg'),
    featureBullets: [
      '输入电源：AC220V±10％ 50Hz',
      '整体外形尺寸：1450mm×1000mm×900mm',
      '装置容量：＜2kVA',
      '安全保护：具有漏电保护，安全符合国家标准',
    ],
    products: [
      {
        name: '工业机器人系统运维实训平台',
        desc: '工业机器人本体、控制柜与示教盒一体化运维平台。',
        image: img('img-p12-5.jpeg'),
        load: '＜2kVA',
        model: '1450×1000×900mm',
      },
    ],
  }),

  'sensor-box': createCategory({
    id: 'sensor-box',
    specType: 'education',
    detailMode: 'equipment',
    label: '典型传感器技术实验箱',
    parentLabel: '教育类',
    heroTitle: '典型传感器技术实验箱',
    heroDesc: '采用模块化设计，传感器结构透明、可拆装，可加深学生对传感器原理与结构的认识。',
    heroImage: img('img-p13-2.jpeg'),
    featureBullets: [
      '实验箱尺寸不小于518mm×408mm×240mm',
      '支持应变片、差动变压器、霍尔、温度等多项实验',
      '适合基础传感器教学和实验认知',
    ],
    products: [
      {
        name: '典型传感器技术实验箱',
        desc: '面向传感器原理与基础实验的教学装置。',
        image: img('img-p13-2.jpeg'),
        load: '待补充',
        model: '518×408×240mm',
        specGroups: [
          {
            name: '实验项目',
            rows: [
              ['实验内容', '金属箔式应变片、差动变压器、电容式传感器、霍尔、温度测量等'],
              ['实验箱尺寸', '518mm×408mm×240mm（不小于）'],
              ['输入电源', '待补充'],
            ],
          },
        ],
      },
    ],
  }),

  'sensor-lab': createCategory({
    id: 'sensor-lab',
    specType: 'education',
    detailMode: 'equipment',
    label: '工业传感器综合实验装置',
    parentLabel: '教育类',
    heroTitle: '工业传感器综合实验装置',
    heroDesc: '将工业传感器和工业模型有机结合的新一代传感器应用技术及实验设备。',
    heroImage: img('img-p13-4.jpeg'),
    featureBullets: [
      '输入电源：单相三线220V±10% 50Hz',
      '装置容量：180VA',
      '外形尺寸：1600mm×800mm×1400mm',
      '适合传感器应用技术和工业模型演示教学',
    ],
    products: [
      {
        name: '工业传感器综合实验装置',
        desc: '工业传感器综合应用平台。',
        image: img('img-p13-4.jpeg'),
        load: '180VA',
        model: '1600×800×1400mm',
      },
    ],
  }),

  electrician: createCategory({
    id: 'electrician',
    specType: 'education',
    detailMode: 'equipment',
    label: '高级电工实训考核装置',
    parentLabel: '教育类',
    heroTitle: '高级电工实训考核装置',
    heroDesc: '依据人社部职业技能鉴定标准设计，配备智能考核系统，满足高级电工实训考核需求。',
    heroImage: img('img-p14-4.jpeg'),
    featureBullets: [
      '工作电源：三相五线供电 AC380V±10% 50Hz',
      '外形尺寸：880×880×1880mm',
      '负荷容量：≤1kVA',
      '具有过流、过载、漏电保护措施',
    ],
    products: [
      {
        name: '高级电工实训考核装置',
        desc: '配备智能考核系统的高级电工实训平台。',
        image: img('img-p14-4.jpeg'),
        load: '≤1kVA',
        model: '880×880×1880mm',
      },
    ],
  }),

  'electric-electronic': createCategory({
    id: 'electric-electronic',
    specType: 'education',
    detailMode: 'equipment',
    label: '电工电子技术综合实验装置',
    parentLabel: '教育类',
    heroTitle: '电工电子技术综合实验装置',
    heroDesc: '采用模块化挂箱结构，电源和仪器仪表独立挂箱，创新模块采用透明外壳，可自由搭建各种电路。',
    heroImage: img('img-p14-2.jpeg'),
    featureBullets: [
      '输入电源：三相四线（或三相五线）～380V±10% 50Hz',
      '装置容量：＜1.5kVA',
      '外形尺寸：1600mm×720mm×1560mm',
      '适合电工电子课程基础实验与创新搭建',
    ],
    products: [
      {
        name: '电工电子技术综合实验装置',
        desc: '电工电子综合实验平台。',
        image: img('img-p14-2.jpeg'),
        load: '＜1.5kVA',
        model: '1600×720×1560mm',
      },
    ],
  }),

  'network-platform': createCategory({
    id: 'network-platform',
    specType: 'education',
    detailMode: 'equipment',
    label: '生产系统网络与通讯运维实践平台',
    parentLabel: '教育类',
    heroTitle: '生产系统网络与通讯运维实践平台',
    heroDesc:
      '包含工业网络单元、网络管理单元和设备监控系统，支持 PROFINET、PROFIBUS 等工业网络通讯实验。',
    heroImage: img('img-p15-2.jpeg'),
    featureBullets: [
      '输入电源：三相四线～380V±10% 50Hz',
      '装机容量：＜1.5kVA',
      '外形尺寸：1600mm×720mm×1650mm',
      '安全保护：具有漏电保护装置',
    ],
    products: [
      {
        name: '生产系统网络与通讯运维实践平台',
        desc: '工业网络与通讯运维实训平台。',
        image: img('img-p15-2.jpeg'),
        load: '＜1.5kVA',
        model: '1600×720×1650mm',
      },
    ],
  }),

  'plc-trainer': createCategory({
    id: 'plc-trainer',
    specType: 'education',
    detailMode: 'equipment',
    label: '可编程控制器实训装置',
    parentLabel: '教育类',
    heroTitle: '可编程控制器实训装置',
    heroDesc:
      '包含 PLC 控制系统、触摸屏、变频系统和步进系统，可满足电气控制、传感器、变频、步进和 PROFINET 实训。',
    heroImage: img('img-p16-2.jpeg'),
    featureBullets: [
      '输入电源：三相四线～380V±10% 50Hz',
      '装机容量：＜1.5kVA',
      '外形尺寸：1600mm×720mm×1650mm',
      '安全保护：具有漏电保护装置',
    ],
    products: [
      {
        name: '可编程控制器实训装置',
        desc: 'PLC、触摸屏、变频与步进控制实训平台。',
        image: img('img-p16-2.jpeg'),
        load: '＜1.5kVA',
        model: '1600×720×1650mm',
      },
    ],
  }),

  'mechanical-assembly': createCategory({
    id: 'mechanical-assembly',
    specType: 'education',
    detailMode: 'equipment',
    label: '机械装调技能实训考核装置',
    parentLabel: '教育类',
    heroTitle: '机械装调技能实训考核装置',
    heroDesc: '按世赛和国赛标准设计，采用积木式结构，可训练多种传动机构安装和机械精度调整能力。',
    heroImage: img('img-p16-4.jpeg'),
    featureBullets: [
      '工作电源：三相五线 AC380V±10%',
      '外形尺寸不小于1500mm×800mm×1450mm',
      '具有漏电保护、短路保护和接地保护等功能',
    ],
    products: [
      {
        name: '机械装调技能实训考核装置',
        desc: '适合机构安装、机械精度调整和技能考核。',
        image: img('img-p16-4.jpeg'),
        load: '待补充',
        model: '1500×800×1450mm',
      },
    ],
  }),

  'welding-station': createCategory({
    id: 'welding-station',
    specType: 'education',
    detailMode: 'equipment',
    label: '焊接机器人工作站',
    parentLabel: '教育类',
    heroTitle: '焊接机器人工作站',
    heroDesc:
      '由工业机器人、焊接电源、焊枪、三维柔性焊接平台和实训套件等组成，可训练多种焊接路径与工艺。',
    heroImage: img('img-p17-4.jpeg'),
    featureBullets: [
      '输入电源：三相四线 AC380V±10% 50Hz',
      '装置容量：≤25kVA',
      '占地空间：约4000mm×3000mm×1800mm',
      '安全保护：漏电、短路、过载保护装置',
    ],
    products: [
      {
        name: '焊接机器人工作站',
        desc: '支持直线、往复、月牙、圆圈等多种焊接方法实训。',
        image: img('img-p17-4.jpeg'),
        load: '≤25kVA',
        model: '4000×3000×1800mm',
      },
    ],
  }),

  'digital-resource': createCategory({
    id: 'digital-resource',
    specType: 'education',
    detailMode: 'service',
    label: '数字资源定制开发',
    parentLabel: '教育类',
    heroTitle: '数字资源定制开发',
    heroDesc: '围绕数字教材解决方案、平台建设和数字教材服务展开的资源定制开发服务。',
    heroImage: pdfPage(21),
    featureBullets: [
      '数字教材出版、数字教材平台、数字教材服务三位一体',
      '支持多终端同步阅读、智能 AI 教学助手和全场景教学应用',
      '可提供数字资源建设、教材申报与知识培训支持',
    ],
    products: [
      {
        name: '数字教材解决方案',
        desc: '包含数字教材出版、数字教材平台和数字教材服务三大方向。',
        image: pdfPage(21),
        load: '服务型交付',
        model: '资源解决方案',
        specGroups: [
          {
            name: '解决方案',
            rows: [
              ['数字教材出版', '知名出版机构、专业编辑团队、严格出版流程'],
              ['数字教材平台', '多终端同步阅读、智能 AI 教学助手、全场景教学应用'],
              ['数字教材服务', '规划教材申报、数字资源建设、专业知识培训'],
            ],
          },
        ],
      },
      {
        name: '数字教材平台与阅读服务',
        desc: '支持教材平台建设、同步阅读和教学服务联动。',
        image: pdfPage(22),
        load: '平台建设',
        model: '教材平台',
        specGroups: [
          {
            name: '占位参数',
            rows: [
              ['终端支持', 'Android / iOS / Windows / macOS / PC / Pad / 手机'],
              ['平台账号规模', '待补充'],
              ['内容资源数量', '待补充'],
            ],
          },
        ],
      },
    ],
  }),

  'digital-textbook': createCategory({
    id: 'digital-textbook',
    specType: 'education',
    detailMode: 'service',
    label: '数字教材定制开发出版',
    parentLabel: '教育类',
    heroTitle: '数字教材定制开发出版',
    heroDesc: '整合选题、项目启动、内容创编、写作指导、资源制作、三审三校和出版发行流程。',
    heroImage: pdfPage(23),
    featureBullets: [
      '支持从选题论证到正式出版发行的全流程服务',
      '内容创编、资源制作、样章审核和质量检查一体推进',
      '适用于院校教材开发、课程资源建设和出版配套',
    ],
    products: [
      {
        name: '数字教材定制出版流程服务',
        desc: '宣传册展示了数字教材定制开发出版的 10 个关键流程步骤。',
        image: pdfPage(23),
        load: '全流程服务',
        model: '出版流程服务',
        specGroups: [
          {
            name: '流程步骤',
            rows: [
              ['流程 1-3', '选题论证、需求确认、项目启动'],
              ['流程 4-7', '内容创编、资源制作、写作指导、三审三校'],
              ['流程 8-10', '编辑制作、质量检查、出版发行'],
            ],
          },
        ],
      },
      {
        name: '数字教材平台功能模块',
        desc: '包含教师端、学生端、教务管理端、数据分析系统和教材发行系统。',
        image: pdfPage(24),
        load: '平台规划',
        model: '功能模块设计',
        specGroups: [
          {
            name: '功能模块',
            rows: [
              ['用户服务系统', '教师端、学生端、教务管理端，跨平台终端支持'],
              ['教学应用系统', '阅读平台、作业考试、学习报告、资源统计等'],
              ['数据分析系统', '阅读时间、答题情况、学习进度、用户活跃度'],
            ],
          },
        ],
      },
    ],
  }),

  'teaching-library': createCategory({
    id: 'teaching-library',
    specType: 'education',
    detailMode: 'service',
    label: '专业教学资源库开发',
    parentLabel: '教育类',
    heroTitle: '专业教学资源库开发',
    heroDesc:
      '对标国家建设标准，以院校特色专业为基础，为客户提供课程体系与优质课程建设服务，打造具有院校特色的资源库。',
    heroImage: pdfPage(25),
    featureBullets: [
      '支持校级、区域性和全国性资源库建设',
      '资源类型覆盖微课、仿真、试题、文本、视频与动画',
      '提供资源申报、建设、上传与推广服务',
    ],
    products: [
      {
        name: '专业教学资源库建设解决方案',
        desc: '面向国家级、区域性和校级教学资源库建设的解决方案。',
        image: pdfPage(25),
        load: '资源库建设',
        model: '教学资源库',
        specGroups: [
          {
            name: '建设内容',
            rows: [
              ['资源库形态', '校级资源库、区域性资源库、全国性资源库'],
              ['资源类型', '微课、仿真、试题、文本、视频、动画资源'],
              ['建设服务', '资源申报、资源建设、资源上传、资源推广'],
            ],
          },
        ],
      },
      {
        name: '教学资源库项目案例',
        desc: '宣传册展示了国家级专业教学资源库项目案例与建设方向。',
        image: pdfPage(26),
        load: '案例展示',
        model: '资源库案例',
        specGroups: [
          {
            name: '案例说明',
            rows: [
              ['内容来源', '宣传册第 23 页案例部分'],
              ['适用院校', '职业院校、本科院校、培训基地'],
              ['详细清单', '可根据后续资料继续扩充'],
            ],
          },
        ],
      },
    ],
  }),

  'competition-support': createCategory({
    id: 'competition-support',
    specType: 'education',
    detailMode: 'service',
    label: '技能大赛技术支持与专家指导',
    parentLabel: '教育类',
    heroTitle: '世界职业院校技能大赛技术支持与专家指导',
    heroDesc: '围绕世界职业院校技能大赛等赛事提供设备改造、技术支持和专家团队指导。',
    heroImage: pdfPage(18),
    featureBullets: [
      '提供赛项设备改造、赛题理解、技术训练和专家指导服务',
      '适合院校竞赛备赛、设备升级和实践教学联动',
      '可延展到创新类大赛技术研发合作',
    ],
    products: [
      {
        name: '技能大赛设备改造与专家指导',
        desc: '围绕世界职业院校技能大赛等赛事提供赛项设备与技术支持。',
        image: pdfPage(18),
        load: '专家服务',
        model: '赛事支持',
        gallery: [pdfPage(18), pdfPage(19), pdfPage(20)],
        specGroups: [
          {
            name: '服务内容',
            rows: [
              ['支持范围', '设备改造、技术培训、赛项指导、专家服务'],
              ['适用对象', '职业院校、本科院校、竞赛培训基地'],
              ['服务形式', '驻场支持 / 方案咨询 / 联合备赛'],
            ],
          },
        ],
      },
      {
        name: '创新类大赛技术研发合作',
        desc: '宣传册明确给出了创新类大赛中的专业需求对接、创新培养和成果转化方向。',
        image: pdfPage(27),
        load: '研发合作',
        model: '创新类大赛',
        specGroups: [
          {
            name: '合作重点',
            rows: [
              ['专业需求对接', '根据院校专业特点提供定制化技术研发支持'],
              ['创新能力培养', '组织创新工作坊、技术研讨会议，激发创新思维'],
              ['成果转化应用', '共同研发产品技术，沉淀教材与在线课程平台成果'],
            ],
          },
        ],
      },
    ],
  }),

  'research-cooperation': createCategory({
    id: 'research-cooperation',
    specType: 'education',
    detailMode: 'service',
    label: '高校及职业院校科研技术合作',
    parentLabel: '教育类',
    heroTitle: '高校及职业院校科研技术合作',
    heroDesc: '围绕校企合作、横向课题、专利技术、新工艺和新产品转化等方向开展科研与技术合作。',
    heroImage: pdfPage(28),
    featureBullets: [
      '实现企业与院校的优势互补、资源共享和技术协同',
      '促进教师科研成果转化、学生实践能力提升与企业创新提速',
      '合作成果多以专利技术、新工艺和新产品等形式输出',
    ],
    products: [
      {
        name: '校企科研技术合作服务',
        desc: '支持横向课题、科研合作、项目申报和技术成果转化。',
        image: pdfPage(28),
        load: '合作服务',
        model: '校企科研合作',
        specGroups: [
          {
            name: '合作方向',
            rows: [
              ['合作目标', '优势互补、资源共享、共同攻克技术难题'],
              ['合作收益', '提升技能、加速创新、降低研发成本、缩短研发周期'],
              ['成果形式', '专利技术、新工艺、新产品、教学资源成果'],
            ],
          },
        ],
      },
    ],
  }),
}

export const defaultCategoryId = 'software'

export function getCategoryById(id) {
  return productCategories[id] || productCategories[defaultCategoryId]
}

export function findProduct(categoryId, productSlug) {
  const category = productCategories[categoryId]
  if (!category) return null
  const product = category.products.find((p) => p.slug === productSlug)
  if (!product) return null
  return { category, product }
}

export function getRelatedProducts(categoryId, productSlug, limit = 4) {
  const category = productCategories[categoryId]
  if (!category) return []
  const current = category.products.find((p) => p.slug === productSlug)
  const pool = category.products.filter((p) => p.slug !== productSlug)
  if (current?.systemPackId) {
    const same = pool.filter((p) => p.systemPackId === current.systemPackId)
    const rest = pool.filter((p) => p.systemPackId !== current.systemPackId)
    return [...same, ...rest].slice(0, limit)
  }
  return pool.slice(0, limit)
}

export function buildProductDetailMeta(category, product) {
  const enriched = enrichProduct(category, product)
  const isEducation = category.specType === 'education' && category.detailMode === 'equipment'
  const isSystem = enriched.productKind === 'system'
  const loadLabel = category.loadLabel || (isEducation ? '装置容量' : '服务类型')
  const modelLabel = category.modelLabel || (isEducation ? '平台尺寸' : '方案类型')

  const iconCycle = ['load', 'model', 'type', 'service']
  const highlights = enriched.highlightParams
    ? enriched.highlightParams.map((h, i) => ({
        icon: iconCycle[i % iconCycle.length],
        value: h.value,
        label: h.label,
      }))
    : enriched.highlights || [
        { icon: 'load', value: enriched.load, label: loadLabel },
        { icon: 'model', value: enriched.model, label: modelLabel },
        { icon: 'type', value: category.label, label: '所属分类' },
        {
          icon: isEducation ? 'safe' : 'service',
          value: isEducation ? '符合国标' : isSystem ? '自研平台' : '可定制',
          label: isEducation ? '安全保护' : '交付方式',
        },
      ]

  const sourcePoints = enriched.detailPoints || category.featureBullets || []
  const featureItems = sourcePoints.slice(0, 6).map((text, index) => {
    const parts = text.split(/[：:,，；;]/)
    return {
      title: parts[0]?.trim() || `特点 ${index + 1}`,
      desc: text,
      icon: ['vision', 'nav', 'controller', 'software', 'sensor', 'cloud'][index % 6],
    }
  })

  const rawSpecGroups = enriched.specGroups || [
    {
      name: '基本参数',
      rows: [
        ['产品名称', enriched.name],
        [loadLabel, enriched.load],
        [modelLabel, enriched.model],
        ['所属系列', category.label],
      ],
    },
    {
      name: isEducation ? '技术指标' : '功能说明',
      rows: sourcePoints.map((point, index) => [`说明 ${index + 1}`, point]),
    },
  ]

  const specGroups = sanitizeSpecGroups(rawSpecGroups)

  return {
    tagline: enriched.tagline || enriched.desc,
    modelCode: enriched.model,
    highlights,
    featureItems,
    specGroups,
    specTabs: enriched.specTabs || ['标准版'],
    intro: enriched.intro,
    introPoints: enriched.introPoints || [],
    applications: enriched.applications || [],
    deliveryItems: enriched.deliveryItems || [],
  }
}

export function findCategoryLabel(id) {
  for (const group of productSidebar) {
    const child = group.children.find((item) => item.id === id)
    if (child) return child.label
  }
  return ''
}

export const navLabelToCategoryId = {
  工业软件系统定制: 'software',
  电能风能与新能源系统: 'energy-system',
  工业互联网平台: 'iiot',
  技能大赛技术支持与专家指导: 'competition-support',
  数字资源定制开发: 'digital-resource',
  数字教材定制开发出版: 'digital-textbook',
  专业教学资源库开发: 'teaching-library',
  高校及职业院校科研技术合作: 'research-cooperation',
  人型机器人: 'humanoid',
  运维机器狗: 'quadruped',
  人工智能技术定制开发: 'ai-custom',
  人工智能产品开发: 'ai-custom',
  无人机产品定制开发: 'drone',
  AGV产品定制开发: 'agv',
  移动机器人产品定制开发: 'mobile-platform',
  自动化产线非标定制: 'automation-line',
  原创工业级产品: 'original-products',
  案例效果图: 'case-showcase',
  主要服务客户: 'service-clients',
  智能智造产线应用综合实训平台: 'smart-line',
  '工业机器人技能实训装置（埃斯顿）': 'estun-trainer',
  智能制造自动化生产线综合实训系统: 'auto-line-trainer',
  工业机器人系统运维实训平台: 'robot-ops',
  典型传感器技术实验箱: 'sensor-box',
  工业传感器综合实验装置: 'sensor-lab',
  高级电工实训考核装置: 'electrician',
  电工电子技术综合实验装置: 'electric-electronic',
  生产系统网络与通讯运维实践平台: 'network-platform',
  可编程控制器实训装置: 'plc-trainer',
  机械装调技能实训考核装置: 'mechanical-assembly',
  焊接机器人工作站: 'welding-station',
}

/** 全站产品搜索 */
export function searchProducts(query, limit = 12) {
  const q = String(query || '').trim().toLowerCase()
  if (!q) return []

  const results = []
  for (const [categoryId, category] of Object.entries(productCategories)) {
    const groupLabel = category.parentLabel || findCategoryLabel(categoryId)
    for (const product of category.products || []) {
      const haystack = [product.name, product.desc, product.model, category.label, groupLabel]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      if (haystack.includes(q)) {
        results.push({
          name: product.name,
          desc: product.desc,
          image: product.image,
          categoryId,
          categoryLabel: category.label,
          groupLabel,
          slug: product.slug || slugify(product.name),
        })
      }
    }
  }
  return results.slice(0, limit)
}

function slugify(text) {
  return String(text)
    .trim()
    .toLowerCase()
    .replace(/[^\w\u4e00-\u9fff]+/g, '-')
    .replace(/^-+|-+$/g, '')
}
